import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { CTASection } from "../components/CTASection";
import { INDUSTRIES, MORE_INDUSTRIES } from "../data/site";

const PRIMARY = ["accounting", "architecture"]; // representative of the two focus verticals

export default function Industries() {
  return (
    <div data-testid="industries-page">
      <Seo
        title="Managed IT Services by Industry in Atlanta | Zentryx Systems"
        description="Managed IT and cybersecurity for Atlanta businesses across every industry — with specialized expertise for CPA/accounting and architecture/engineering firms, plus law, healthcare, professional and financial services."
        path="/industries"
      />
      <section className="pt-32 sm:pt-40 pb-16 px-5 sm:px-8 bg-mist">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Industries</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-charcoal leading-tight">
              Managed IT for Atlanta businesses.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              We work with small and midsize businesses across every industry in the Atlanta metro — and we bring deep, specialized expertise to CPA/accounting and architecture/engineering firms, where we understand your deadlines, data, and workflows.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.slug} delay={(i % 2) * 0.08}>
              <Link to={ind.detailPath}
                className="group grid lg:grid-cols-2 gap-10 items-center rounded-3xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg hover:shadow-slate-200/60 transition-all">
                <div className={`h-72 lg:h-full overflow-hidden ${i % 2 ? "lg:order-2" : ""}`}>
                  <img src={ind.img} alt={`IT support for ${ind.title} in Atlanta`}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-8 sm:p-12">
                  {PRIMARY.includes(ind.slug) && (
                    <span className="inline-block text-xs uppercase tracking-widest font-semibold text-emerald bg-emerald/10 px-3 py-1 rounded-full mb-4">Specialized experience</span>
                  )}
                  <div className="h-12 w-12 rounded-2xl bg-navy/5 flex items-center justify-center mb-5">
                    <ind.icon className="h-6 w-6 text-navy" />
                  </div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-charcoal">{ind.title}</h2>
                  <p className="mt-4 text-slate-600 leading-relaxed">{ind.body}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 font-semibold text-navy group-hover:gap-2.5 transition-all">
                    Explore IT for {ind.title.replace(" Firms", "")} <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ADDITIONAL INDUSTRIES — permanent content, same card style as primary */}
      <section className="pb-20 sm:pb-28 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {MORE_INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.title} delay={(i % 2) * 0.08}>
              <div data-testid={`industries-more-${i}`} id={ind.anchor}
                className="group grid lg:grid-cols-2 gap-10 items-center rounded-3xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg hover:shadow-slate-200/60 transition-all scroll-mt-28">
                <div className={`h-72 lg:h-full overflow-hidden ${i % 2 ? "lg:order-2" : ""}`}>
                  <img src={ind.img} alt={`IT support for ${ind.title} in Atlanta`}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-8 sm:p-12">
                  <div className="h-12 w-12 rounded-2xl bg-navy/5 flex items-center justify-center mb-5">
                    <ind.icon className="h-6 w-6 text-navy" />
                  </div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-charcoal">{ind.title}</h2>
                  <p className="mt-4 text-slate-600 leading-relaxed">{ind.body}</p>
                  <div className="mt-5 space-y-2.5">
                    {ind.points.map((p) => (
                      <div key={p} className="flex items-start gap-3">
                        <span className="mt-0.5 h-5 w-5 rounded-full bg-emerald/10 flex items-center justify-center flex-shrink-0">
                          <Check className="h-3 w-3 text-emerald" />
                        </span>
                        <span className="text-sm text-charcoal">{p}</span>
                      </div>
                    ))}
                  </div>
                  <Link to={ind.detailPath} data-testid={`industries-more-link-${i}`}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:gap-2.5 transition-all">
                    Explore IT for {ind.title} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <CTASection />
    </div>
  );
}
